import { ChevronLeft, ChevronRight, Loader2, Search } from "lucide-react";

/**
 * Tabla PAGINADA de destinatarios de una campaña de email, con buscador y
 * filtro por estado. Reutilizada por `EmailCampaigns.tsx` (campañas de
 * negocio, `GET /email-campaigns/:id/stats`) y `AdminCampaigns.tsx` (campañas
 * de plataforma, `GET /admin/campaigns/:id/stats`) — los dos endpoints
 * comparten el mismo contrato de respuesta (helper `buildCampaignStatsResponse`
 * en el backend), así que la tabla también puede compartirse tal cual.
 *
 * El componente es "tonto": no hace fetch ni debounce, solo pinta lo que le
 * pasan y notifica cambios de página/tamaño/búsqueda/filtro vía callbacks. La
 * página que lo usa es responsable de debounced el texto de búsqueda antes de
 * llamar a `onSearchValueChange` (mismo patrón que `fetchAudienceStats` en
 * `EmailCampaigns.tsx`) y de volver a pedir datos al backend cuando cambia
 * cualquiera de los filtros.
 */

/** Filtro de estado de la tabla paginada (mismas 6 opciones que acepta el backend). */
export type RecipientStatusFilter =
  | "all"
  | "sent"
  | "opened"
  | "clicked"
  | "failed"
  | "unsubscribed"
  | "reserved";

/** Opciones del selector de filtro, en el orden en que se muestran. */
export const RECIPIENT_STATUS_FILTER_OPTIONS: Array<{
  value: RecipientStatusFilter;
  label: string;
}> = [
  { value: "all", label: "Todos" },
  { value: "sent", label: "Enviados" },
  { value: "opened", label: "Abiertos" },
  { value: "clicked", label: "Con clic" },
  { value: "failed", label: "Fallidos" },
  { value: "unsubscribed", label: "Dados de baja" },
  { value: "reserved", label: "Reservaron" },
];

/** Tamaños de página que ofrece el selector (el backend clampa a 100 como máximo). */
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

/** Destinatario de campaña, tal y como lo devuelve `GET .../:id/stats` (página actual). */
export interface CampaignStatsRecipient {
  id: string;
  name: string | null;
  email: string;
  status: string;
  sentAt: string | null;
  openedAt: string | null;
  clickedAt: string | null;
  /**
   * Baja atribuida a ESTA campaña. Se pinta SIEMPRE a partir de este campo,
   * NUNCA del `status` (el backend deja el status intacto tras la baja —
   * sigue diciendo "sent" — para no distorsionar las tasas de apertura/clic).
   */
  unsubscribedAt: string | null;
  /**
   * Reserva atribuida a ESTA campaña (clic en el botón del email → widget →
   * reserva). Mismo criterio que la baja: se pinta por timestamp, no por status.
   */
  reservedAt: string | null;
  failedReason: string | null;
}

function formatDateTime(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("es-ES", {
    timeStyle: "short",
    dateStyle: "short",
  });
}

function RecipientStatusBadge({
  status,
  unsubscribedAt,
  reservedAt,
}: {
  status: string;
  unsubscribedAt: string | null;
  reservedAt: string | null;
}) {
  // La baja manda sobre cualquier otro estado visual: un recipient "sent" que
  // luego se dio de baja debe verse como "Dado de baja", no como "Enviado".
  if (unsubscribedAt) {
    return (
      <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
        Dado de baja
      </span>
    );
  }
  // La conversión es el segundo estado más informativo: quien reservó desde el
  // email vale más que "abierto" o "clic".
  if (reservedAt) {
    return (
      <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
        Reservó
      </span>
    );
  }

  const config: Record<string, { label: string; bg: string; text: string }> = {
    pending: { label: "Pendiente", bg: "bg-gray-100", text: "text-gray-600" },
    sent: { label: "Enviado", bg: "bg-green-100", text: "text-green-700" },
    failed: { label: "Fallido", bg: "bg-red-100", text: "text-red-700" },
    opened: { label: "Abierto", bg: "bg-blue-100", text: "text-blue-700" },
    clicked: { label: "Clic", bg: "bg-purple-100", text: "text-purple-700" },
    skipped: { label: "Omitido", bg: "bg-gray-100", text: "text-gray-500" },
  };
  const c = config[status] || config.pending;
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${c.bg} ${c.text}`}
    >
      {c.label}
    </span>
  );
}

export interface CampaignRecipientsTableProps {
  recipients: CampaignStatsRecipient[];
  /** Total de destinatarios que casan el filtro/búsqueda actual (para paginar). */
  total: number;
  page: number;
  limit: number;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  /** Valor tal cual escrito en el buscador (sin debounce: lo aplica la página). */
  searchValue: string;
  onSearchValueChange: (value: string) => void;
  status: RecipientStatusFilter;
  onStatusChange: (status: RecipientStatusFilter) => void;
  /**
   * Refetch en curso (cambio de página/búsqueda/filtro). A propósito NO
   * sustituye la tabla por un spinner de página completa: solo se ve un
   * indicador junto al título para no perder el scroll ni el contexto.
   */
  loading?: boolean;
  /** Etiqueta de la primera columna: "Destinatario" (negocio) o "Hostelero" (plataforma). */
  recipientLabel?: string;
}

export default function CampaignRecipientsTable({
  recipients,
  total,
  page,
  limit,
  onPageChange,
  onLimitChange,
  searchValue,
  onSearchValueChange,
  status,
  onStatusChange,
  loading = false,
  recipientLabel = "Destinatario",
}: CampaignRecipientsTableProps) {
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const isFiltered = searchValue.trim() !== "" || status !== "all";
  const emptyMessage = isFiltered
    ? "Ningún destinatario coincide con la búsqueda o el filtro"
    : "Sin destinatarios aún";

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="px-4 md:px-6 py-3 border-b border-gray-200 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
          Destinatarios
          {loading && (
            <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
          )}
        </h2>
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              value={searchValue}
              onChange={(e) => onSearchValueChange(e.target.value)}
              placeholder="Buscar por nombre o email..."
              className="w-full sm:w-56 pl-8 pr-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>
          <select
            value={status}
            onChange={(e) =>
              onStatusChange(e.target.value as RecipientStatusFilter)
            }
            className="border border-gray-300 rounded-lg text-sm px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-primary-500"
          >
            {RECIPIENT_STATUS_FILTER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden divide-y divide-gray-100">
        {recipients.length === 0 ? (
          <div className="p-8 text-center text-gray-400 text-sm">
            {emptyMessage}
          </div>
        ) : (
          recipients.map((r) => (
            <div key={r.id} className="p-3">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {r.name || "Sin nombre"}
                  </p>
                  <p className="text-xs text-gray-500 truncate">{r.email}</p>
                </div>
                <RecipientStatusBadge
                  status={r.status}
                  unsubscribedAt={r.unsubscribedAt}
                  reservedAt={r.reservedAt}
                />
              </div>
              {r.sentAt && (
                <p className="text-xs text-gray-400 mt-1">
                  Enviado: {formatDateTime(r.sentAt)}
                </p>
              )}
              {r.unsubscribedAt && (
                <p className="text-xs text-red-500 mt-0.5">
                  Baja: {formatDateTime(r.unsubscribedAt)}
                </p>
              )}
              {r.reservedAt && (
                <p className="text-xs text-emerald-600 mt-0.5">
                  Reserva: {formatDateTime(r.reservedAt)}
                </p>
              )}
            </div>
          ))
        )}
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
            <tr>
              <th className="px-6 py-3 text-left">{recipientLabel}</th>
              <th className="px-6 py-3 text-left">Email</th>
              <th className="px-6 py-3 text-left">Estado</th>
              <th className="px-6 py-3 text-left">Enviado</th>
              <th className="px-6 py-3 text-left">Abierto</th>
              <th className="px-6 py-3 text-left">Clic</th>
              <th className="px-6 py-3 text-left">Baja</th>
              <th className="px-6 py-3 text-left">Reserva</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {recipients.length === 0 ? (
              <tr>
                <td
                  colSpan={8}
                  className="px-6 py-8 text-center text-gray-400 text-sm"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              recipients.map((r) => (
                <tr key={r.id} className="hover:bg-gray-50">
                  <td className="px-6 py-3 text-sm text-gray-900">
                    {r.name || "—"}
                  </td>
                  <td className="px-6 py-3 text-sm text-gray-600">
                    {r.email}
                  </td>
                  <td className="px-6 py-3">
                    <RecipientStatusBadge
                      status={r.status}
                      unsubscribedAt={r.unsubscribedAt}
                      reservedAt={r.reservedAt}
                    />
                  </td>
                  <td className="px-6 py-3 text-xs text-gray-500">
                    {formatDateTime(r.sentAt)}
                  </td>
                  <td className="px-6 py-3 text-xs text-gray-500">
                    {formatDateTime(r.openedAt)}
                  </td>
                  <td className="px-6 py-3 text-xs text-gray-500">
                    {formatDateTime(r.clickedAt)}
                  </td>
                  <td className="px-6 py-3 text-xs text-red-500">
                    {formatDateTime(r.unsubscribedAt)}
                  </td>
                  <td className="px-6 py-3 text-xs text-emerald-600">
                    {formatDateTime(r.reservedAt)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Footer */}
      <div className="px-4 md:px-6 py-3 border-t border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-sm text-gray-500">
        <span>
          {total === 0
            ? "0 destinatarios"
            : `Mostrando ${(page - 1) * limit + 1}-${Math.min(page * limit, total)} de ${total}`}
        </span>
        <div className="flex items-center gap-2">
          <select
            value={limit}
            onChange={(e) => onLimitChange(Number(e.target.value))}
            className="border border-gray-300 rounded-lg text-sm px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary-500"
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>
                {size} / página
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => onPageChange(Math.max(1, page - 1))}
            disabled={page <= 1}
            className="p-1.5 border border-gray-300 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
            aria-label="Página anterior"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-gray-600 whitespace-nowrap">
            Página {page} de {totalPages}
          </span>
          <button
            type="button"
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages}
            className="p-1.5 border border-gray-300 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
            aria-label="Página siguiente"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
